import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import { ROLES } from '../../../_configuration';
import { rejectGraphQLErrors } from '../helpers/graphql-error.helpers';
import {
  optimisticIncludeNonConcluded,
  optimisticReportCardAccess,
  optimisticReportCardAvailability,
} from '../helpers/report-card-access.helpers';
import { useLogQueryError } from '../hooks/use-log-query-error';
import { UPDATE_AGE_GROUP_INCLUDE_NON_CONCLUDED } from '../queries/age-group.queries';
import { REFRESH_REPORT_CARD_ACCESS_TOKENS } from '../queries/email-log.queries';
import {
  GET_PLAYER_REPORT_CARD,
  GET_REPORT_CARD_AVAILABILITY,
  REPORT_CARD_AVAILABILITY_FIELDS,
  REPORT_CARD_SEND_PREVIEW,
  SEND_REPORT_CARDS,
  SET_AGE_GROUP_REPORT_CARD_ACCESS,
  SET_PLAYER_REPORT_CARD_ACCESS,
} from '../queries/report-card-access.queries';
import { useCompanyReportCardsSetting } from '../stores/company.store';
import { COMMUNICATION_LIST_QUERIES } from './email-log.service';
import { getCurrMember } from './member.service';
import { hasAnyRoles, memberHasAnyRoles } from './user.service';

// Queries whose rows show a player's report card status or contact links. A send or a link
// extension changes server-issued tokens the client can't derive, so these are refetched.
export const REPORT_CARD_ROW_QUERIES = ['GetPlayerRankings', 'GetPlayerReportCard'];

// The age group's status counts, a server aggregate that any player's release or revoke changes.
export const REPORT_CARD_AVAILABILITY_QUERIES = ['GetReportCardAvailability'];

// Every send adds to the player's messages. A first send also releases players, which changes the counts;
// a resend can't. The send dialog's preview refetches itself each time the dialog opens, so it isn't listed.
export const REPORT_CARD_RESEND_QUERIES = [...REPORT_CARD_ROW_QUERIES, ...COMMUNICATION_LIST_QUERIES];
export const REPORT_CARD_SEND_QUERIES = [...REPORT_CARD_RESEND_QUERIES, ...REPORT_CARD_AVAILABILITY_QUERIES];

type Typed<T> = T & { __typename?: string };

type AgeGroupRef = Pick<IAgeGroup, '_id' | 'assessmentId'>;

interface ISetPlayerReportCardAccessData {
  setPlayerReportCardAccess: Typed<{
    playerAssessments: Typed<{ _id: string; reportCardAccess?: Typed<IReportCardAccess> | null }>[];
    notReleasedPlayerAssessmentIds: string[];
  }>;
}

// Hook: CONFIGURATION on the player's age group, which the server requires to send, resend, revoke or
// re-enable. See "Render visibility and data visibility are one decision" in CLAUDE.md.
export const canManageReportCards = (ageGroupId?: string): boolean =>
  hasAnyRoles({ roles: [ROLES.CONFIGURATION], ageGroupId });

export interface IReportCardSurface {
  associationOff: boolean;
  sectionVisible: boolean;
  canManage: boolean;
  canOpen: boolean;
}

// Hook: what a player's report card surfaces show for this user in any age group, from one member read and
// computed once per age group. See "Report cards off for an association still allow one player's send" in CLAUDE.md.
export const useReportCardSurfaceResolver = () => {
  const setting = useCompanyReportCardsSetting();
  const { member } = getCurrMember();

  return useMemo(() => {
    const byAgeGroup = new Map<string, IReportCardSurface>();
    // Every report card role is scoped to an age group, so without one nothing shows.
    const unscoped = { associationOff: setting === false, sectionVisible: false, canManage: false, canOpen: false };

    return (ageGroupId?: string): IReportCardSurface => {
      if (!ageGroupId) return unscoped;
      const known = byAgeGroup.get(ageGroupId);
      if (known) return known;

      const has = (roles: string[]) => memberHasAnyRoles({ member, roles, ageGroupId });
      // CONFIGURATION sends, revokes and re-enables; REPORTS or CONFIGURATION reads the status; REPORTS
      // opens the staff report card page, whose data needs it.
      const canManage = has([ROLES.CONFIGURATION]);
      const surface = {
        associationOff: setting === false,
        // Null (association still loading) shows nothing rather than guessing.
        sectionVisible: setting === true ? has([ROLES.REPORTS, ROLES.CONFIGURATION]) : setting === false && canManage,
        canManage,
        // The staff report card page redirects while the association has report cards off.
        canOpen: setting === true && has([ROLES.REPORTS]),
      };
      byAgeGroup.set(ageGroupId, surface);
      return surface;
    };
  }, [setting, member]);
};

// Hook: the report card surface for one age group.
export const useReportCardSurface = (ageGroupId?: string): IReportCardSurface => useReportCardSurfaceResolver()(ageGroupId);

// One player's report card status, with everything ReportCardActions needs. Mount only behind useReportCardSurface's sectionVisible.
export const usePlayerReportCard = (playerAssessmentId: string) => {
  const { data, loading, error } = useQuery<{ playerAssessment: IPlayerAssessment | null }>(GET_PLAYER_REPORT_CARD, {
    variables: { _id: playerAssessmentId },
    fetchPolicy: 'cache-and-network',
  });
  useLogQueryError(error, 'playerReportCard');

  return { playerAssessment: data?.playerAssessment, loading, error };
};

export const useReportCardAvailability = (ageGroup: AgeGroupRef) => {
  const { data, loading, error, refetch } = useQuery<{ reportCardAvailability: IReportCardAvailability }>(GET_REPORT_CARD_AVAILABILITY, {
    variables: { assessmentId: ageGroup.assessmentId, ageGroupId: ageGroup._id },
    fetchPolicy: 'cache-and-network',
  });
  useLogQueryError(error, 'reportCardAvailability');

  return { availability: data?.reportCardAvailability, loading, error, refetch };
};

// A first send to one age group with includeNonConcluded as shown, so the numbers confirmed are the numbers
// sent. `loading` stays true while cached numbers are refreshed; a confirm waits for it.
export const useReportCardSendPreview = ({ ageGroup, includeNonConcluded, skip }: {
  ageGroup: AgeGroupRef;
  includeNonConcluded?: boolean;
  skip: boolean;
}) => {
  const { data, loading, error, refetch } = useQuery<{ reportCardSendPreview: IReportCardSendPreview }>(REPORT_CARD_SEND_PREVIEW, {
    variables: { assessmentId: ageGroup.assessmentId, ageGroupIds: [ageGroup._id], includeNonConcluded },
    skip: skip || includeNonConcluded === undefined,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  useLogQueryError(error, 'reportCardSendPreview');

  return { preview: data?.reportCardSendPreview, loading, error, refetch };
};

export const buildSetPlayerReportCardAccess = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation] = useMutation<ISetPlayerReportCardAccessData>(SET_PLAYER_REPORT_CARD_ACCESS, {
    refetchQueries: REPORT_CARD_AVAILABILITY_QUERIES,
  });

  return {
    setPlayerReportCardAccess: (playerAssessment: IPlayerAssessment, enabled: boolean) => mutation({
      variables: {
        assessmentId: playerAssessment.ageGroup.assessmentId,
        playerAssessmentIds: [playerAssessment._id],
        enabled,
      },
      optimisticResponse: playerAssessment.reportCardAccess ? {
        setPlayerReportCardAccess: {
          __typename: 'SetPlayerReportCardAccessResult',
          playerAssessments: [{
            __typename: 'PlayerAssessment',
            _id: playerAssessment._id,
            reportCardAccess: { __typename: 'ReportCardAccess', ...optimisticReportCardAccess(playerAssessment.reportCardAccess, enabled) },
          }],
          notReleasedPlayerAssessmentIds: [],
        },
      } : undefined,
    }),
  };
};

// Returns the age group's availability, which the cache keys by age group, so the panel updates in
// place; the rows' chips and an open send preview are server-derived and refetched.
export const buildSetAgeGroupReportCardAccess = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation] = useMutation<{ setAgeGroupReportCardAccess: Typed<IReportCardAvailability> }>(SET_AGE_GROUP_REPORT_CARD_ACCESS, {
    refetchQueries: [...REPORT_CARD_ROW_QUERIES, 'ReportCardSendPreview'],
  });

  return {
    setAgeGroupReportCardAccess: (ageGroup: AgeGroupRef, availability: IReportCardAvailability, enabled: boolean) => mutation({
      variables: { assessmentId: ageGroup.assessmentId, ageGroupId: ageGroup._id, enabled },
      optimisticResponse: {
        setAgeGroupReportCardAccess: { __typename: 'ReportCardAvailability', ...optimisticReportCardAvailability(availability, enabled) },
      },
    }),
  };
};

// Saves the age group's own choice (null = follow the default). The availability is written in the
// optimistic layer, so a failed save reverts it; clearing the choice reads the resolved default back.
export const buildSetAgeGroupIncludeNonConcluded = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation] = useMutation(UPDATE_AGE_GROUP_INCLUDE_NON_CONCLUDED);

  return {
    setAgeGroupIncludeNonConcluded: (availability: IReportCardAvailability, value: boolean | null) => mutation({
      variables: { _id: availability.ageGroupId, value },
      optimisticResponse: {
        updateAgeGroup: { __typename: 'AgeGroup', _id: availability.ageGroupId, reportCardsIncludeNonConcluded: value },
      },
      update: (cache) => {
        const id = cache.identify({ __typename: 'ReportCardAvailability', ageGroupId: availability.ageGroupId });
        if (!id) return;
        cache.writeFragment({
          id,
          fragment: REPORT_CARD_AVAILABILITY_FIELDS,
          data: { __typename: 'ReportCardAvailability', ...optimisticIncludeNonConcluded(availability, value) },
        });
      },
      refetchQueries: value === null ? REPORT_CARD_AVAILABILITY_QUERIES : [],
      awaitRefetchQueries: true,
    }),
  };
};

export const buildExtendReportCardLinks = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation] = useMutation(REFRESH_REPORT_CARD_ACCESS_TOKENS, { refetchQueries: REPORT_CARD_ROW_QUERIES });

  return {
    extendReportCardLinks: (playerAssessmentId: string) => mutation({ variables: { playerAssessmentId } }),
  };
};

interface ISendReportCardsVariables {
  assessmentId: string;
  ageGroupIds: string[];
  playerAssessmentIds?: string[];
  resend?: boolean;
  // Absent, the server uses each age group's effective setting.
  includeNonConcluded?: boolean;
}

// A first send or a resend, resolving to who it told. A partial send still rejects (SEND_INCOMPLETE), and
// errorPolicy 'all' refetches the views for the part that did go out.
export const buildSendReportCards = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation] = useMutation<{ sendReportCards: IReportCardSendResult }>(SEND_REPORT_CARDS, { errorPolicy: 'all' });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sendReportCards = useCallback(async (variables: ISendReportCardsVariables): Promise<IReportCardSendResult> => {
    const { data } = rejectGraphQLErrors(await mutation({
      variables,
      refetchQueries: variables.resend ? REPORT_CARD_RESEND_QUERIES : REPORT_CARD_SEND_QUERIES,
    }));
    if (!data) throw new Error('sendReportCards returned no result.');
    return data.sendReportCards;
  }, [mutation]);

  return { sendReportCards };
};
